import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  Users, Plus, Loader2, Search, Gift, AlertCircle,
  Trophy, Home, Phone, Mail, Calendar, TrendingUp,
  Star, ChevronRight, Building2, Filter, Download
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  booked: 'bg-blue-100 text-blue-700',
  agreement_signed: 'bg-purple-100 text-purple-700',
  under_construction: 'bg-yellow-100 text-yellow-700',
  ready_for_possession: 'bg-orange-100 text-orange-700',
  possessed: 'bg-green-100 text-green-700'
};

const tierColors = {
  bronze: 'bg-orange-100 text-orange-700',
  silver: 'bg-gray-200 text-gray-700',
  gold: 'bg-yellow-100 text-yellow-700',
  platinum: 'bg-purple-100 text-purple-700'
};

export default function BuyersManagementPage() {
  const [buyers, setBuyers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterProject, setFilterProject] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');

  const [newBuyer, setNewBuyer] = useState({
    name: '',
    email: '',
    phone: '',
    project_id: '',
    unit_number: '',
    tower_block: '',
    floor: '',
    property_type: 'apartment',
    booking_date: '',
    purchase_value: ''
  });

  const { user } = useAuth();
  const builderId = user?.builder_id;

  useEffect(() => {
    if (builderId) {
      loadData();
    }
  }, [builderId]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Load buyers
      const buyersRes = await fetch(`${API}/api/buyer-portal/admin/buyers?builder_id=${builderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (buyersRes.ok) {
        const data = await buyersRes.json();
        setBuyers(data);
      }

      // Load dashboard stats
      const dashRes = await fetch(`${API}/api/buyer-portal/admin/dashboard?builder_id=${builderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (dashRes.ok) {
        const data = await dashRes.json();
        setDashboard(data);
      }

      // Load projects
      const projRes = await fetch(`${API}/api/projects?builder_id=${builderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (projRes.ok) {
        const data = await projRes.json();
        setProjects(data);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBuyer = async () => {
    if (!newBuyer.name || !newBuyer.email || !newBuyer.phone || !newBuyer.project_id || !newBuyer.unit_number) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/buyer-portal/admin/buyers?builder_id=${builderId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newBuyer,
          floor: newBuyer.floor ? parseInt(newBuyer.floor) : null,
          purchase_value: newBuyer.purchase_value ? parseFloat(newBuyer.purchase_value) : null
        })
      });

      if (res.ok) {
        toast.success('Buyer created successfully! Default password is "admin"');
        setShowCreateDialog(false);
        setNewBuyer({
          name: '', email: '', phone: '', project_id: '', unit_number: '',
          tower_block: '', floor: '', property_type: 'apartment', booking_date: '', purchase_value: ''
        });
        loadData();
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to create buyer');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Filter and sort buyers
  const filteredBuyers = buyers
    .filter(buyer => {
      if (filterStatus !== 'all' && buyer.status !== filterStatus) return false;
      if (filterProject !== 'all' && buyer.project_id !== filterProject) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          buyer.name?.toLowerCase().includes(query) ||
          buyer.email?.toLowerCase().includes(query) ||
          buyer.phone?.includes(query) ||
          buyer.unit_number?.toLowerCase().includes(query)
        );
      }
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'referrals':
          return (b.successful_referrals || 0) - (a.successful_referrals || 0);
        case 'points':
          return (b.loyalty_points || 0) - (a.loyalty_points || 0);
        case 'created_at':
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Buyers Management</h1>
            <p className="text-muted-foreground">Manage your property buyers and track their engagement</p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)} className="bg-amber-600 hover:bg-amber-700">
            <Plus className="h-4 w-4 mr-2" />
            Add Buyer
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard?.buyers?.total || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Buyers</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Home className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard?.buyers?.by_status?.possessed || 0}</p>
                  <p className="text-xs text-muted-foreground">Possessed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Gift className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard?.referrals?.total || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Referrals</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard?.referrals?.conversion_rate || 0}%</p>
                  <p className="text-xs text-muted-foreground">Conversion</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard?.complaints?.open || 0}</p>
                  <p className="text-xs text-muted-foreground">Open Tickets</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, email, phone, unit..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="booked">Booked</SelectItem>
                  <SelectItem value="agreement_signed">Agreement Signed</SelectItem>
                  <SelectItem value="under_construction">Under Construction</SelectItem>
                  <SelectItem value="ready_for_possession">Ready for Possession</SelectItem>
                  <SelectItem value="possessed">Possessed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterProject} onValueChange={setFilterProject}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">Newest First</SelectItem>
                  <SelectItem value="name">Name A-Z</SelectItem>
                  <SelectItem value="referrals">Top Referrers</SelectItem>
                  <SelectItem value="points">Highest Points</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Buyers List */}
        <Card>
          <CardHeader>
            <CardTitle>Buyers ({filteredBuyers.length})</CardTitle>
            <CardDescription>Performance and engagement metrics</CardDescription>
          </CardHeader>
          <CardContent>
            {filteredBuyers.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium text-lg mb-2">No buyers found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchQuery || filterStatus !== 'all' || filterProject !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Add your first buyer to get started'}
                </p>
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Buyer
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-3 font-medium text-muted-foreground">Buyer</th>
                      <th className="pb-3 font-medium text-muted-foreground">Unit</th>
                      <th className="pb-3 font-medium text-muted-foreground">Status</th>
                      <th className="pb-3 font-medium text-muted-foreground text-center">Tier</th>
                      <th className="pb-3 font-medium text-muted-foreground text-center">Referrals</th>
                      <th className="pb-3 font-medium text-muted-foreground text-center">Points</th>
                      <th className="pb-3 font-medium text-muted-foreground text-center">Rented</th>
                      <th className="pb-3 font-medium text-muted-foreground"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBuyers.map((buyer) => (
                      <tr key={buyer.id} className="border-b hover:bg-muted/50">
                        <td className="py-3">
                          <div>
                            <p className="font-medium">{buyer.name}</p>
                            <p className="text-sm text-muted-foreground">{buyer.email}</p>
                            <p className="text-xs text-muted-foreground">{buyer.phone}</p>
                          </div>
                        </td>
                        <td className="py-3">
                          <div>
                            <p className="font-medium">{buyer.unit_number}</p>
                            <p className="text-xs text-muted-foreground">{buyer.tower_block}</p>
                          </div>
                        </td>
                        <td className="py-3">
                          <Badge className={statusColors[buyer.status]}>
                            {buyer.status?.replace(/_/g, ' ')}
                          </Badge>
                        </td>
                        <td className="py-3 text-center">
                          <Badge className={tierColors[buyer.loyalty_tier] || tierColors.bronze}>
                            {buyer.loyalty_tier?.toUpperCase() || 'BRONZE'}
                          </Badge>
                        </td>
                        <td className="py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Gift className="h-4 w-4 text-green-600" />
                            <span className="font-semibold">{buyer.successful_referrals || 0}</span>
                            <span className="text-muted-foreground text-sm">/ {buyer.total_referrals || 0}</span>
                          </div>
                        </td>
                        <td className="py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Star className="h-4 w-4 text-amber-500" />
                            <span className="font-semibold">{buyer.loyalty_points || 0}</span>
                          </div>
                        </td>
                        <td className="py-3 text-center">
                          {buyer.is_rented ? (
                            <Badge className="bg-purple-100 text-purple-700">Yes</Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="py-3">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setSelectedBuyer(buyer);
                              setShowDetailDialog(true);
                            }}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Performers */}
        {buyers.length > 0 && (
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-amber-600" />
                  Top Referrers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {buyers
                    .filter(b => (b.successful_referrals || 0) > 0)
                    .sort((a, b) => (b.successful_referrals || 0) - (a.successful_referrals || 0))
                    .slice(0, 5)
                    .map((buyer, i) => (
                      <div key={buyer.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                        <div className="flex items-center gap-3">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            i === 0 ? 'bg-yellow-400 text-yellow-900' :
                            i === 1 ? 'bg-gray-300 text-gray-700' :
                            i === 2 ? 'bg-orange-300 text-orange-800' : 'bg-muted'
                          }`}>
                            {i + 1}
                          </span>
                          <div>
                            <p className="font-medium text-sm">{buyer.name}</p>
                            <p className="text-xs text-muted-foreground">{buyer.unit_number}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-600">{buyer.successful_referrals}</p>
                          <p className="text-xs text-muted-foreground">referrals</p>
                        </div>
                      </div>
                    ))}
                  {buyers.filter(b => (b.successful_referrals || 0) > 0).length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">No referrals yet</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  Recent Complaints
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboard?.recent_complaints?.slice(0, 5).map((complaint) => (
                    <div key={complaint.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                      <div>
                        <p className="font-medium text-sm">{complaint.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {complaint.buyer_name} • Unit {complaint.unit_number}
                        </p>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {complaint.status}
                      </Badge>
                    </div>
                  ))}
                  {(!dashboard?.recent_complaints || dashboard.recent_complaints.length === 0) && (
                    <p className="text-sm text-muted-foreground text-center py-4">No complaints</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Create Buyer Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Buyer</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Full Name *</Label>
                <Input
                  placeholder="Enter buyer name"
                  value={newBuyer.name}
                  onChange={(e) => setNewBuyer({ ...newBuyer, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  placeholder="buyer@email.com"
                  value={newBuyer.email}
                  onChange={(e) => setNewBuyer({ ...newBuyer, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Phone *</Label>
                <Input
                  placeholder="+91 9876543210"
                  value={newBuyer.phone}
                  onChange={(e) => setNewBuyer({ ...newBuyer, phone: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Project *</Label>
              <Select
                value={newBuyer.project_id}
                onValueChange={(v) => setNewBuyer({ ...newBuyer, project_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Unit Number *</Label>
                <Input
                  placeholder="A-101"
                  value={newBuyer.unit_number}
                  onChange={(e) => setNewBuyer({ ...newBuyer, unit_number: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Tower/Block</Label>
                <Input
                  placeholder="Tower A"
                  value={newBuyer.tower_block}
                  onChange={(e) => setNewBuyer({ ...newBuyer, tower_block: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Floor</Label>
                <Input
                  type="number"
                  placeholder="10"
                  value={newBuyer.floor}
                  onChange={(e) => setNewBuyer({ ...newBuyer, floor: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Property Type</Label>
                <Select
                  value={newBuyer.property_type}
                  onValueChange={(v) => setNewBuyer({ ...newBuyer, property_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="apartment">Apartment</SelectItem>
                    <SelectItem value="villa">Villa</SelectItem>
                    <SelectItem value="plot">Plot</SelectItem>
                    <SelectItem value="penthouse">Penthouse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Booking Date</Label>
                <Input
                  type="date"
                  value={newBuyer.booking_date}
                  onChange={(e) => setNewBuyer({ ...newBuyer, booking_date: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Purchase Value (₹)</Label>
              <Input
                type="number"
                placeholder="8500000"
                value={newBuyer.purchase_value}
                onChange={(e) => setNewBuyer({ ...newBuyer, purchase_value: e.target.value })}
              />
            </div>

            <div className="p-3 bg-amber-50 rounded-lg text-sm">
              <p className="text-amber-800">
                <strong>Note:</strong> The buyer will receive login access with default password "admin". 
                They should change it on first login.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateBuyer} 
              disabled={submitting}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Buyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Buyer Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Buyer Details</DialogTitle>
          </DialogHeader>

          {selectedBuyer && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <span className="text-xl font-bold text-amber-700">
                    {selectedBuyer.name?.charAt(0)}
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{selectedBuyer.name}</h3>
                  <p className="text-sm text-muted-foreground">{selectedBuyer.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Phone</p>
                  <p className="font-medium">{selectedBuyer.phone}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Unit</p>
                  <p className="font-medium">{selectedBuyer.unit_number} {selectedBuyer.tower_block && `• ${selectedBuyer.tower_block}`}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <Badge className={statusColors[selectedBuyer.status]}>
                    {selectedBuyer.status?.replace(/_/g, ' ')}
                  </Badge>
                </div>
                <div>
                  <p className="text-muted-foreground">Loyalty Tier</p>
                  <Badge className={tierColors[selectedBuyer.loyalty_tier]}>
                    {selectedBuyer.loyalty_tier?.toUpperCase()}
                  </Badge>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Performance</h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{selectedBuyer.successful_referrals || 0}</p>
                    <p className="text-xs text-muted-foreground">Successful Referrals</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-2xl font-bold text-amber-600">{selectedBuyer.loyalty_points || 0}</p>
                    <p className="text-xs text-muted-foreground">Points Earned</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{selectedBuyer.total_referrals || 0}</p>
                    <p className="text-xs text-muted-foreground">Total Referrals</p>
                  </div>
                </div>
              </div>

              {selectedBuyer.purchase_value && (
                <div className="border-t pt-4">
                  <p className="text-muted-foreground text-sm">Purchase Value</p>
                  <p className="text-xl font-bold">₹{selectedBuyer.purchase_value?.toLocaleString()}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
